import {
  area as turfArea,
  buffer as turfBuffer,
  circle as turfCircle,
  length as turfLength,
  point as turfPoint,
} from '@turf/turf';
import type { LineString, MultiPolygon, Polygon } from 'geojson';
import * as _ from 'lodash';
import { map, round } from 'lodash';
import cgColors from '~/colors';
import { getCircleDestCoords } from '~/components2/Map/canvas/circle/common';
import { type CircleObject, type LineObject, type MapObject, ProjectGeometryType } from './types';

export function area(
  geojson: GeoJSON.Polygon,
  // , units: 'meters' | 'acres'
) {
  const areaInMeters = turfArea(geojson);
  // TODO: Implement unit conversion
  return {
    value: round(areaInMeters, 2),
    // See https://en.wikipedia.org/wiki/Unicode_subscripts_and_superscripts
    units: 'm\u00B2',
  };
}

export function length(geojson: GeoJSON.LineString, units: 'meters' | 'feet') {
  const lengthInMeters: number = turfLength({ type: 'Feature', geometry: geojson, properties: {} }, { units });
  let lengthInFeet: number;

  const returnedUnits = units === 'meters' ? 'm' : 'ft';

  if (units === 'feet') {
    lengthInFeet = 3.28084 * lengthInMeters;
    return {
      value: round(lengthInFeet, 2),
      units: returnedUnits,
    };
  } else {
    return {
      value: round(lengthInMeters, 2),
      units: returnedUnits,
    };
  }
}

export function isEmpty(geojson: GeoJSON.MultiLineString | GeoJSON.MultiPolygon) {
  return _.isEmpty(geojson.coordinates);
}

export function createCircleFromPoint({
  center,
  radius,
}: {
  center: GeoJSON.Point;
  radius: number; // in feet
}) {
  const rad = radius / 5_280; // feet to miles
  const coords = center.coordinates;
  const p = turfPoint(coords);
  const circle = turfCircle(p, rad, {
    steps: 100,
    units: 'miles',
  });

  return circle.geometry;
}

export function getMapObjectsAsGeoJSON(
  objectsAsArray: MapObject[],
  geometryType?: ProjectGeometryType,
): (GeoJSON.MultiLineString | GeoJSON.MultiPolygon) & {
  properties: { [key: string]: any };
} {
  if (geometryType === ProjectGeometryType.Linear) {
    return {
      type: 'MultiLineString',
      properties: { segmentNames: objectsAsArray.map((o) => o.title) },
      coordinates: map(objectsAsArray, (o: MapObject) => (o.geometry as LineString).coordinates),
    };
  } else if (geometryType === ProjectGeometryType.Circle) {
    return {
      type: 'MultiLineString',
      properties: { segmentName: objectsAsArray.map((o) => o.title) },
      coordinates: map(objectsAsArray, (o: MapObject) => {
        const c = o as CircleObject;
        const radius = c.properties.radius; // pass in as feet, fn handles conversion
        const coords = c.geometry.coordinates;
        const destCoords = getCircleDestCoords({
          lngLat: { lng: coords[0], lat: coords[1] },
          radius,
        }).geometry.coordinates;

        const center = c.geometry.coordinates;
        return [center, destCoords];
      }),
    };
  } else {
    return {
      type: 'MultiPolygon',
      properties: { segmentNames: objectsAsArray.map((o) => o.title) },
      coordinates: map(objectsAsArray, (o: MapObject) => (o.geometry as Polygon).coordinates),
    };
  }
}

export type GeomToMultiPolygonOptions = {
  geometryType?: ProjectGeometryType;
  objectsAsArray: MapObject[];
};

export function geomToMultiPolygon(props: GeomToMultiPolygonOptions) {
  const { geometryType, objectsAsArray } = props;
  if (geometryType === ProjectGeometryType.Linear) {
    const multiLine = getMapObjectsAsGeoJSON(objectsAsArray, geometryType);
    const bufferOffset = (objectsAsArray[0] as LineObject)?.properties?.buffer || 40;
    const bufferedFeature = turfBuffer(multiLine, bufferOffset, {
      units: 'feet',
    });

    if (!bufferedFeature) return;

    let bufferedPolygon = bufferedFeature.geometry;
    // all polygons need to be MultiPolygon
    if (bufferedPolygon.type === 'Polygon') {
      bufferedPolygon = {
        type: 'MultiPolygon',
        coordinates: [bufferedPolygon.coordinates],
      };
    }

    return bufferedPolygon as MultiPolygon;
  } else if (geometryType === ProjectGeometryType.Circle) {
    const circleMultiPolygon: GeoJSON.MultiPolygon = {
      type: 'MultiPolygon',
      coordinates: map(
        objectsAsArray as CircleObject[],
        (o: CircleObject) =>
          createCircleFromPoint({
            center: o.geometry,
            radius: o.properties.radius,
          }).coordinates,
      ),
    };

    return circleMultiPolygon as MultiPolygon;
  } else {
    return getMapObjectsAsGeoJSON(objectsAsArray, geometryType) as MultiPolygon;
  }
}

export function createFeature(geometry: GeoJSON.Geometry): GeoJSON.Feature {
  return {
    type: 'Feature',
    geometry,
    properties: {
      stroke: cgColors.brand,
      'stroke-width': 4,
      fill: cgColors.brand,
      'fill-opacity': 0.25,
    },
  };
}
